import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const layers = [
  {
    level: '底层',
    title: '单课评价',
    items: ['综合总分', '维度分', '分档结果', '证据链', '改进建议'],
    color: 'primary' as const,
    icon: 'ri-file-list-3-line',
  },
  {
    level: '中层',
    title: '四类使用对象',
    items: ['教师', '学生', '专家', '管理者'],
    color: 'accent' as const,
    icon: 'ri-user-shared-line',
  },
  {
    level: '上层',
    title: '治理结果',
    items: ['教师成长', '教研改进', '学校质量', '区域治理'],
    color: 'primary' as const,
    icon: 'ri-government-line',
  },
];

export default function P60FromAI2Empower({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-16 right-20 w-80 h-80 rounded-full bg-primary-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-24 left-12 w-96 h-96 rounded-full bg-accent-100/6 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-16 pb-8 md:pb-12">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />第五部分 · 应用服务与质量治理
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从<span className="text-primary-600">AI评课能力</span>到<span className="text-accent-600">教学质量治理</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-snug transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            评价不是终点，评价结果进入教学行动和治理流程才产生持续价值
          </p>
        </div>

        {/* Three Column Layout */}
        <div className={`max-w-5xl mx-auto w-full mb-5 md:mb-6 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-stretch">
            {layers.map((layer, li) => (
              <div key={layer.level} className="relative flex flex-col">
                {/* Arrow between columns (desktop only) */}
                {li > 0 && (
                  <div className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 items-center">
                    <i className="ri-arrow-right-line text-sm text-foreground-300" />
                  </div>
                )}

                <div className="flex-1 p-4 md:p-5 glass-liquid rounded-xl text-center">
                  <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
                    <span className="text-xs font-mono font-bold text-gold-700 bg-gold-50/80 px-2 py-0.5 rounded-full border border-gold-200/40">{layer.level}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg ${layer.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                      <i className={`${layer.icon} text-sm ${layer.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                    </div>
                    <span className={`font-heading text-sm md:text-base font-bold ${layer.color === 'accent' ? 'text-gold-700' : 'text-primary-700'}`}>{layer.title}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {layer.items.map((item) => (
                      <span
                        key={item}
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-body font-semibold ${
                          layer.color === 'accent'
                            ? 'bg-gold-100/80 text-gold-700 border border-gold-300/30'
                            : 'bg-primary-100/60 text-primary-700 border border-primary-200/30'
                        }`}
                        style={{ transitionDelay: `${300 + li * 150}ms` }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Arrow below on mobile */}
                {li < layers.length - 1 && (
                  <div className="flex md:hidden justify-center py-1.5">
                    <i className="ri-arrow-down-line text-sm text-foreground-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Data chain connector */}
          <div className={`flex justify-center mt-5 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center gap-1 px-4 py-2 glass rounded-full border border-gold-200/30">
              <i className="ri-database-2-line text-xs text-gold-600" />
              <span className="text-xs font-body text-foreground-500">课堂事实不断汇聚、应用层级逐步提升</span>
              <i className="ri-arrow-up-line text-xs text-gold-600" />
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <div className={`max-w-3xl mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed text-center">
              一份AI评课报告不应停留在系统中，也不能只作为一次性分数。它首先应当帮助教师复盘课堂，帮助学生找到重点，帮助专家快速进入关键证据，也帮助管理者从大量课程中看清整体结构。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}